<template>
  <div class="quick-search-field" :style="maxWidth ? { maxWidth } : undefined">
    <v-icon icon="mdi-magnify" size="20" class="quick-search-field__icon" />
    <input
      ref="inputRef"
      class="quick-search-field__input"
      type="search"
      :value="modelValue ?? ''"
      :placeholder="placeholder"
      :aria-label="ariaLabel || placeholder"
      autocomplete="off"
      @input="onInput"
      @keydown.enter="onEnter"
    >
    <v-btn
      v-if="!!(modelValue && String(modelValue).length)"
      icon="mdi-close"
      size="x-small"
      variant="text"
      aria-label="Clear search"
      @click="clear"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'

defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Quick search' },
  ariaLabel: { type: String, default: '' },
  maxWidth: { type: String, default: '420px' },
})

const emit = defineEmits(['update:modelValue', 'keydown'])

const inputRef = ref(null)

function onInput (e) {
  emit('update:modelValue', e.target.value ?? '')
}

function onEnter (e) {
  // Re-emit so parents can use @keydown.enter without changing search logic.
  emit('keydown', e)
}

function clear () {
  emit('update:modelValue', '')
}

defineExpose({
  focus: () => inputRef.value?.focus(),
})
</script>

<style scoped>
.quick-search-field {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 240px;
  min-width: 200px;
  width: 100%;
  background: var(--app-input-bg, #fff);
  border: 1px solid var(--app-border-strong, #d0d7de);
  border-radius: 8px;
  padding: 4px 10px;
  box-sizing: border-box;
}

.quick-search-field__icon {
  color: var(--app-link, #0b7ea4);
  flex: 0 0 auto;
}

.quick-search-field__input {
  flex: 1 1 auto;
  border: 0;
  outline: none;
  background: transparent;
  font-family: 'Source Sans 3', 'Segoe UI', sans-serif;
  font-size: 1rem;
  color: var(--app-text, #1b242c);
  min-width: 0;
}

.quick-search-field__input::placeholder {
  color: var(--app-text-muted, #8a9aa6);
}

/* Hide native search clear so we use the Vuetify clear button */
.quick-search-field__input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}
</style>
